import React, { useEffect, useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Ticket, Filter, Search, Plus, ToggleLeft, ToggleRight, Copy, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin coupon management with quick filter tabs (all / active / expired /
 * exhausted / most-used). Uses GET /api/admin/coupons + PUT toggle + POST
 * create.
 */
export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/coupons");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Could not load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const now = new Date();
  // Derive per-coupon state for filters
  const decorated = useMemo(() => items.map((c) => {
    const usedCount = c.used_count || 0;
    const limit = c.usage_limit ?? null;
    const isExpired = c.expires_at && new Date(c.expires_at) < now;
    const isExhausted = limit !== null && usedCount >= limit;
    const isActive = !!c.active && !isExpired && !isExhausted;
    return { ...c, _usedCount: usedCount, _isExpired: isExpired, _isExhausted: isExhausted, _isActive: isActive };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [items]);

  const counts = useMemo(() => ({
    all: decorated.length,
    active: decorated.filter((c) => c._isActive).length,
    expired: decorated.filter((c) => c._isExpired).length,
    exhausted: decorated.filter((c) => c._isExhausted && !c._isExpired).length,
    inactive: decorated.filter((c) => !c.active && !c._isExpired && !c._isExhausted).length,
  }), [decorated]);

  const filtered = useMemo(() => {
    let out = decorated;
    if (filter === "active")    out = out.filter((c) => c._isActive);
    else if (filter === "expired")   out = out.filter((c) => c._isExpired);
    else if (filter === "exhausted") out = out.filter((c) => c._isExhausted && !c._isExpired);
    else if (filter === "inactive")  out = out.filter((c) => !c.active && !c._isExpired && !c._isExhausted);
    else if (filter === "most_used") out = [...out].sort((a, b) => (b._usedCount || 0) - (a._usedCount || 0));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter((c) => (c.code || "").toLowerCase().includes(q) || (c.source || "").toLowerCase().includes(q));
    }
    if (filter !== "most_used") {
      out = [...out].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return out;
  }, [decorated, filter, query]);

  const toggle = async (code, next) => {
    try {
      await api.put(`/admin/coupons/${code}/toggle`);
      toast.success(next ? `${code} activated` : `${code} deactivated`);
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`${code} copied`); }
    catch { /* best-effort */ }
  };

  const FILTERS = [
    { id: "all",       label: "All",         colour: "bg-slate-100 text-slate-800" },
    { id: "active",    label: "Active",      colour: "bg-emerald-100 text-emerald-800" },
    { id: "most_used", label: "Most used",   colour: "bg-blue-100 text-blue-800" },
    { id: "expired",   label: "Expired",     colour: "bg-rose-100 text-rose-800" },
    { id: "exhausted", label: "Exhausted",   colour: "bg-amber-100 text-amber-800" },
    { id: "inactive",  label: "Inactive",    colour: "bg-slate-200 text-slate-700" },
  ];

  return (
    <Card className="mt-6 p-6 border-slate-200" data-testid="admin-coupons">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-fuchsia-700"/>
          <h2 className="font-heading text-xl font-semibold text-slate-900">Coupons</h2>
          <span className="text-xs text-slate-500">({counts.all})</span>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="h-9 bg-fuchsia-700 hover:bg-fuchsia-800"
          data-testid="admin-coupon-create-btn"
        >
          <Plus className="w-4 h-4 mr-1.5"/> New coupon
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="admin-coupon-filter-tabs">
        <Filter className="w-4 h-4 text-slate-400"/>
        {FILTERS.map((f) => {
          const isActive = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
                isActive
                  ? `${f.colour} border-transparent shadow-sm`
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`admin-coupon-filter-${f.id}`}
            >
              {f.label}
              <span className="ml-1 text-[10px] text-slate-500">
                {f.id === "all" ? counts.all
                  : f.id === "active" ? counts.active
                  : f.id === "expired" ? counts.expired
                  : f.id === "exhausted" ? counts.exhausted
                  : f.id === "inactive" ? counts.inactive
                  : ""}
              </span>
            </button>
          );
        })}
        <div className="ml-auto relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2"/>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code…"
            className="h-8 w-52 pl-8 text-sm"
            data-testid="admin-coupon-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading coupons…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="admin-coupon-empty">
            No coupons match this filter.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-coupon-table">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 pr-3">Code</th>
                <th className="text-left py-2 pr-3">Discount</th>
                <th className="text-left py-2 pr-3">Min order</th>
                <th className="text-left py-2 pr-3">Used</th>
                <th className="text-left py-2 pr-3">Expires</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const discountLabel = c.type === "flat"
                  ? `₹${c.value}`
                  : `${c.value}%${c.max_discount ? ` (max ₹${c.max_discount})` : ""}`;
                const statusPill = c._isExpired
                  ? { text: "Expired", cls: "bg-rose-100 text-rose-700 border-rose-200" }
                  : c._isExhausted
                    ? { text: "Exhausted", cls: "bg-amber-100 text-amber-700 border-amber-200" }
                    : !c.active
                      ? { text: "Inactive", cls: "bg-slate-100 text-slate-700 border-slate-200" }
                      : { text: "Active", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
                return (
                  <tr key={c.code} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-coupon-row-${c.code}`}>
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-900">{c.code}</span>
                        <button
                          className="text-slate-400 hover:text-slate-700"
                          onClick={() => copyCode(c.code)}
                          title="Copy code"
                          data-testid={`admin-coupon-copy-${c.code}`}
                        >
                          <Copy className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                      {c.source && <div className="text-[10px] text-slate-500 mt-0.5">{c.source}</div>}
                    </td>
                    <td className="py-3 pr-3 font-semibold text-slate-800">{discountLabel}</td>
                    <td className="py-3 pr-3 text-slate-600">{c.min_amount ? `₹${c.min_amount}` : "—"}</td>
                    <td className="py-3 pr-3 text-slate-700">
                      <span className="font-semibold">{c._usedCount}</span>
                      {c.usage_limit ? <span className="text-slate-400"> / {c.usage_limit}</span> : null}
                    </td>
                    <td className="py-3 pr-3 text-slate-600 text-xs">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "No expiry"}
                    </td>
                    <td className="py-3 pr-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${statusPill.cls}`}>
                        {statusPill.text}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => toggle(c.code, !c.active)}
                        data-testid={`admin-coupon-toggle-${c.code}`}
                      >
                        {c.active ? <ToggleRight className="w-3.5 h-3.5 mr-1 text-emerald-600"/> : <ToggleLeft className="w-3.5 h-3.5 mr-1 text-slate-500"/>}
                        {c.active ? "Deactivate" : "Activate"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CouponCreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </Card>
  );
}

function CouponCreateDialog({ onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: "",
    type: "flat",
    value: 100,
    min_amount: 1000,
    max_discount: "",
    usage_limit: "",
    per_user_limit: 1,
    expires_at: "",
    source: "admin",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.code.trim()) { toast.error("Code required"); return; }
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: Number(form.value) || 0,
        min_amount: Number(form.min_amount) || 0,
        max_discount: form.max_discount ? Number(form.max_discount) : null,
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
        per_user_limit: Number(form.per_user_limit) || 1,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        source: form.source || "admin",
      };
      await api.post("/admin/coupons", payload);
      toast.success(`Created ${payload.code}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center p-4" data-testid="admin-coupon-create-dialog">
      <Card className="w-full max-w-lg p-6 bg-white">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Create coupon</h3>
            <p className="text-xs text-slate-500 mt-0.5">Flat ₹ off or % off — server enforces min-order + per-user cap.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <Field label="Code" testid="coupon-code-field">
            <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="LAUNCH500" className="font-mono"/>
          </Field>
          <Field label="Type">
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="h-9"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat ₹ off</SelectItem>
                <SelectItem value="percent">% off</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={form.type === "flat" ? "Discount ₹" : "Discount %"}>
            <Input type="number" value={form.value} onChange={(e) => set("value", e.target.value)}/>
          </Field>
          <Field label="Min order ₹">
            <Input type="number" value={form.min_amount} onChange={(e) => set("min_amount", e.target.value)}/>
          </Field>
          {form.type === "percent" && (
            <Field label="Max discount ₹">
              <Input type="number" value={form.max_discount} onChange={(e) => set("max_discount", e.target.value)} placeholder="optional"/>
            </Field>
          )}
          <Field label="Total usage limit">
            <Input type="number" value={form.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} placeholder="unlimited"/>
          </Field>
          <Field label="Per-user limit">
            <Input type="number" value={form.per_user_limit} onChange={(e) => set("per_user_limit", e.target.value)}/>
          </Field>
          <Field label="Expires at">
            <Input type="date" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)}/>
          </Field>
          <Field label="Source tag" className="col-span-2">
            <Input value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="admin / campaign / partner"/>
          </Field>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-fuchsia-700 hover:bg-fuchsia-800" data-testid="admin-coupon-create-submit">
            {busy ? "Creating…" : <><CheckCircle2 className="w-4 h-4 mr-1"/> Create</>}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children, className = "", testid }) {
  return (
    <div className={className} data-testid={testid}>
      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
